/**
 * Type definitions for the unified orders system (Manual + WooCommerce)
 */

// ========================================
// Order Status Types
// ========================================

export type ManualOrderStatus =
  | 'draft'       // Bozza, non ancora confermato
  | 'confirmed'   // Confermato, stock prenotato
  | 'processing'  // In lavorazione/preparazione
  | 'prepared'    // Preparato, pronto per spedizione
  | 'shipped'     // Spedito
  | 'invoiced'    // Fatturato
  | 'completed'   // Completato
  | 'cancelled';  // Annullato

export type WooCommerceOrderStatus =
  | 'pending'
  | 'processing'
  | 'on-hold'
  | 'prepared'
  | 'shipped'
  | 'completed'
  | 'cancelled'
  | 'refunded'
  | 'failed'
  | 'trash';

export type OrderChannel = 'manual' | 'woocommerce' | 'shopify';

// ========================================
// Address Types
// ========================================

export interface OrderAddress {
  first_name?: string;
  last_name?: string;
  company?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  email?: string;
  phone?: string;
}

// ========================================
// Order Item Types
// ========================================

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  variation_id?: string;
  product_name: string;
  product_sku?: string;
  sale_unit?: string | null;
  pack_weight_kg?: number | null;
  quantity: number;
  unit_price: number;
  discount_amount: number;
  line_total: number;
  tax_rate: number;
  /** 'custom' = riga libera (prodotto non a catalogo, product_id = prodotto segnaposto) */
  line_type?: string;
  reservation_id?: string;
  sort_order: number;
  notes?: string;
  created_at: string;
  // Relations
  product?: {
    id: string;
    name: string;
    sku: string;
    stock_quantity: number;
    unit_of_measure_1?: string | null;
  };
}

export interface OrderItemInput {
  product_id: string;
  variation_id?: string;
  product_name: string;
  product_sku?: string;
  sale_unit?: string | null;
  pack_weight_kg?: number | null;
  quantity: number;
  unit_price: number;
  discount_amount?: number;
  tax_rate?: number;
  notes?: string;
  /** 'custom' = riga libera (prodotto non a catalogo); assente = 'product' */
  line_type?: 'product' | 'custom';
  zone_id?: string;      // Zona magazzino da cui prelevare
  zone_name?: string;    // Nome zona per display
}

// ========================================
// Manual Order Types
// ========================================

export interface ManualOrder {
  id: string;
  organization_id: string;
  order_number: string;
  status: ManualOrderStatus;
  client_id: string;
  deal_id?: string;
  price_list_id?: string | null;
  subtotal: number;
  discount_amount: number;
  shipping_cost: number;
  tax_amount: number;
  total: number;
  currency: string;
  billing_address?: OrderAddress;
  shipping_address?: OrderAddress;
  /** Sede/indirizzo di spedizione del cliente scelto (client_operational_locations) */
  shipping_location_id?: string | null;
  shipping_method?: string;
  tracking_number?: string;
  carrier?: string;
  customer_notes?: string;
  internal_notes?: string;
  order_date: string;
  confirmed_at?: string;
  shipped_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  cancel_reason?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Relations
  client?: {
    id: string;
    business_name: string;
    email?: string;
    phone?: string;
    vat_number?: string;
    /** client_states.id: Lead o Cliente (pagina in cui si trova la scheda) */
    stato_cliente?: string | null;
  };
  deal?: {
    id: string;
    title: string;
  };
  items?: OrderItem[];
  created_by_user?: {
    id: string;
    full_name: string;
    email: string;
  };
}

export interface CreateManualOrderRequest {
  request_id?: string;
  client_id: string;
  deal_id?: string;
  price_list_id?: string | null;
  billing_address?: OrderAddress;
  shipping_address?: OrderAddress;
  /** Sede/indirizzo di spedizione del cliente scelto (client_operational_locations) */
  shipping_location_id?: string | null;
  shipping_method?: string;
  shipping_cost?: number;
  customer_notes?: string;
  internal_notes?: string;
  items: OrderItemInput[];
  auto_confirm?: boolean; // Se true, conferma subito e prenota stock
}

export interface UpdateManualOrderRequest {
  status?: ManualOrderStatus;
  /** Solo ordini in bozza non nati da un'offerta (modifica completa dal dettaglio) */
  client_id?: string;
  price_list_id?: string | null;
  billing_address?: OrderAddress;
  shipping_address?: OrderAddress;
  /** Sede/indirizzo di spedizione del cliente scelto (client_operational_locations) */
  shipping_location_id?: string | null;
  shipping_method?: string;
  shipping_cost?: number;
  /** null = svuota (tracking facoltativo, modificabile in qualsiasi momento) */
  tracking_number?: string | null;
  carrier?: string | null;
  customer_notes?: string;
  internal_notes?: string;
  cancel_reason?: string;
}

// ========================================
// Unified Order Types (for list view)
// ========================================

export interface UnifiedOrder {
  id: string;
  organization_id: string;
  order_number: string;
  channel: OrderChannel;
  external_id?: number; // woo_order_id per WooCommerce
  /** Numero dell'ordine nel negozio ("113520" su WordPress, "#1001" su Shopify) */
  external_number?: string | null;
  /** Tabella di origine in v_all_orders: 'orders' (manuali e motore) o 'woocommerce_orders' (vecchia integrazione) */
  source_table?: 'orders' | 'woocommerce_orders';
  status: string;
  client_id?: string;
  customer_name?: string;
  customer_email?: string;
  total: number;
  currency: string;
  raw_data?: Record<string, unknown>; // order_data per WooCommerce o dati costruiti per manual
  item_count: number;
  internal_notes?: string;
  deal_id?: string;
  assigned_to?: string; // UUID operatore assegnato
  /** Data dell'ordine (negozi: quella del negozio). Per i vecchi ordini WooCommerce = created_at */
  order_date?: string | null;
  created_at: string;
  updated_at: string;
  // Relations
  client?: {
    id: string;
    business_name: string;
    email?: string;
    vat_number?: string;
  };
  assignee?: {
    id: string;
    full_name: string;
    email: string;
    avatar_url?: string;
  };
}

// ========================================
// Filters and Stats
// ========================================

export interface UnifiedOrderFilters {
  status?: string;
  /** Piu' stati insieme (filtro a scelta multipla); si somma a `status`. */
  statuses?: string[];
  channel?: OrderChannel | 'all';
  clientId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  assignedToId?: string; // Filtro per operatore assegnato
  /** Piu' operatori insieme; 'unassigned' = ordini senza operatore. Si somma a `assignedToId`. */
  assignedToIds?: string[];
  /** Solo gli ordini di un negozio collegato (orders.commerce_store_id), dal link della pagina del negozio. */
  storeId?: string;
}

export interface UnifiedOrderStats {
  total: number;
  woocommerce: number;
  manual: number;
  byStatus: Record<string, number>;
}

// ========================================
// Availability Check
// ========================================

export interface ProductAvailability {
  product_id: string;
  product_name: string;
  stock_quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  requested_quantity: number;
  is_available: boolean;
  shortage?: number;
}

// ========================================
// Draft Order (for creation form)
// ========================================

export interface DraftOrder {
  request_id?: string;
  client_id?: string;
  deal_id?: string;
  price_list_id?: string | null;
  billing_address?: OrderAddress;
  shipping_address?: OrderAddress;
  /** Sede/indirizzo di spedizione del cliente scelto (client_operational_locations) */
  shipping_location_id?: string | null;
  shipping_method?: string;
  shipping_cost: number;
  customer_notes?: string;
  internal_notes?: string;
  items: OrderItemInput[];
  // Computed
  subtotal: number;
  discount_total: number;
  tax_total: number;
  total: number;
}

// ========================================
// Order Status Config
// ========================================

export const MANUAL_ORDER_STATUS_CONFIG: Record<ManualOrderStatus, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Bozza', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  confirmed: { label: 'Confermato', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  processing: { label: 'In preparazione', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  prepared: { label: 'Pronto', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  shipped: { label: 'Spedito', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
  invoiced: { label: 'Fatturato', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  completed: { label: 'Completato', color: 'text-green-700', bgColor: 'bg-green-100' },
  cancelled: { label: 'Annullato', color: 'text-red-700', bgColor: 'bg-red-100' }
};

export const UNIFIED_ORDER_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  // Manual order statuses
  draft: { label: 'Bozza', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  confirmed: { label: 'Confermato', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  // Common statuses
  pending: { label: 'In attesa', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  processing: { label: 'In preparazione', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  'on-hold': { label: 'In sospeso', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  prepared: { label: 'Pronto', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  shipped: { label: 'Spedito', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
  invoiced: { label: 'Fatturato', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  completed: { label: 'Completato', color: 'text-green-700', bgColor: 'bg-green-100' },
  cancelled: { label: 'Annullato', color: 'text-red-700', bgColor: 'bg-red-100' },
  refunded: { label: 'Rimborsato', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  failed: { label: 'Fallito', color: 'text-red-700', bgColor: 'bg-red-100' },
  trash: { label: 'Cestinato', color: 'text-gray-500', bgColor: 'bg-gray-50' }
};

export const CHANNEL_CONFIG: Record<OrderChannel, { label: string; icon: string; color: string }> = {
  manual: { label: 'Manuale', icon: 'FileText', color: 'text-blue-600' },
  woocommerce: { label: 'WooCommerce', icon: 'ShoppingCart', color: 'text-purple-600' },
  shopify: { label: 'Shopify', icon: 'ShoppingCart', color: 'text-purple-600' }
};
